import { SagaIterator } from "redux-saga";
import { call, select, takeLatest } from "typed-redux-saga";
import { AnyAction } from "redux";
import { Action } from "typescript-fsa";

import {
	FormEngineActions,
	FormEngineSelectors
} from "@com.mgmtp.a12.formengine/formengine-core/lib/client-extensions";
import { Events, ModelSelectors } from "@com.mgmtp.a12.formengine/formengine-core/lib/back-end/store";
import { Attachment, JsonRpc2Response } from "@com.mgmtp.a12.dataservices/dataservices-access";
import { DocumentServiceFactory, GroupInstance } from "@com.mgmtp.a12.kernel/kernel-md-facade";
import { Localizable } from "@com.mgmtp.a12.utils/utils-localization/lib/main";

import { assertNonNullable } from "../../../utilities/assert";
import { createGebuehrenforderung, isJsonRpc2ResponseArray } from "../../../utilities/rpcHelper";
import { ModulfDataSelectors } from "../../../utilities/selectors/modulfDataSelectors";
import { entityInstancePathFromA12Path, entityInstancePathFromString } from "../../../utilities/pathHelpers";
import { RESOURCE_KEYS } from "../../../localization";
import { putErrorNotification } from "../../../utilities/notificationUtils";
import { CREATE_GEBUEHRENFORDERUNG } from "../../../utilities/variables/customEventNames";
import { executeWithoutValidation, weakValidation } from "../../../utilities/validationUtils";
import {
	GEBUEHRENFORDERUNG_PATH,
	MAPPING_CONFIGURATION,
	WEAK_VALIDATION_IDENTIFIERS
} from "../../../utilities/variables/modulfRuntimeAnnotations";
import { executeBusy, updateWorkflowsActivityData } from "../../../utilities/workflowUtils";
import { ModulfUiSelectors } from "../../../utilities/selectors/modulfUiSelectors";

import Exception = JsonRpc2Response.Exception;

const GEBUEHREN_FORDERUNG_KEYS = RESOURCE_KEYS.features.buchungsassistent;

const documentService = new DocumentServiceFactory().getDocumentService();

export function* createGebuehrenVorgangSaga(): SagaIterator<void> {
	yield* takeLatest(
		(action: AnyAction) => {
			return (
				FormEngineActions.event.match(action) &&
				Events.eventButton.match(action.payload.engineEvent) &&
				[CREATE_GEBUEHRENFORDERUNG].includes(action.payload.engineEvent.payload.name)
			);
		},
		function* (action: Action<FormEngineActions.FormEngineEventActions<Action<Events.EventButtonPayload>>>) {
			yield* call(executeBusy, function* () {
				const { activityId } = action.payload;
				yield* call(executeWithoutValidation, activityId, function* () {
					const engineState = yield* select(FormEngineSelectors.engineState(activityId));
					assertNonNullable(engineState);
					const documentModel = ModelSelectors.documentModel()(engineState);
					const { buttonPath } = action.payload.engineEvent.payload;
					const annotations = yield* select(
						ModulfUiSelectors.selectButtonAnnotations(activityId, buttonPath)
					);
					const gebuehrenforderungGroupPath = annotations.find(
						it => it.name === GEBUEHRENFORDERUNG_PATH
					)?.value;
					const mappingConfigurationId = annotations.find(it => it.name === MAPPING_CONFIGURATION)?.value;
					const data = yield* select(ModulfDataSelectors.selectTaskDocument(activityId));
					if (!(gebuehrenforderungGroupPath && data && mappingConfigurationId)) {
						return;
					}
					const path = gebuehrenforderungGroupPath.substring(gebuehrenforderungGroupPath.indexOf("/root/"));
					const includeWeakModules = [WEAK_VALIDATION_IDENTIFIERS.GEBUEHRENFORDERUNG];
					const isValid = yield* call(weakValidation, activityId, includeWeakModules);
					if (!isValid) {
						return;
					}
					const attachment = getSelectedAttachment(path, data);
					if (attachment && (attachment.mime_type === "pdf" || attachment.mime_type === "tif")) {
						yield* call(
							putErrorNotification,
							{ key: GEBUEHREN_FORDERUNG_KEYS.error.invalidFile },
							{},
							activityId,
							"gebuehrenforderung"
						);
						return;
					}

					try {
						const updatedDocument = yield* call(
							createGebuehrenforderung,
							documentService.formatDates(data, documentModel),
							data.id,
							path,
							mappingConfigurationId,
							attachment?.attachment_id
						);

						const parsedDocument = {
							...documentService.parseDates(updatedDocument, documentModel),
							modelId: documentModel.header.id
						};
						yield* call(updateWorkflowsActivityData, parsedDocument);
						const url = getVorgangsUrl(path, parsedDocument);

						if (url) {
							window.open(url, "_blank");
						}
					} catch (error) {
						let message: Localizable;
						if (isJsonRpc2ResponseArray(error)) {
							message = extractExceptionMessage(error);
						} else {
							message = {
								key: GEBUEHREN_FORDERUNG_KEYS.error.internalServerError,
								args: { reason: { type: "plain", value: "Unbekannter Fehler." } }
							};
						}
						yield* call(
							putErrorNotification,
							message,
							error,
							action.payload.activityId,
							"gebuehrenforderung"
						);
					}
				});
			});
		}
	);
}

function getSelectedAttachment(pathToGebuehrGroup: string, document: GroupInstance): Attachment | undefined {
	const entityPath = entityInstancePathFromA12Path(pathToGebuehrGroup + "/nachweisDokument");
	const actualPath = documentService.getEntityPathsInDocument(document, entityPath).pop();
	const attachments = actualPath
		? [documentService.getAssignedObject(document, actualPath)]
				.filter((it): it is string => typeof it === "string")
				.map(entityInstancePathFromString)
				.map(it => documentService.getAssignedObject(document, it))
				.filter((attachment): attachment is Attachment => attachment !== undefined)
		: [];
	return attachments.pop();
}

function getVorgangsUrl(pathToGebuehrGroup: string, document: GroupInstance): string | undefined {
	const entityInstancePath = entityInstancePathFromA12Path(pathToGebuehrGroup + "/anlageInformationen/vorgangsUrl");
	const url = documentService.getAssignedObject(document, entityInstancePath);
	return typeof url === "string" ? url : undefined;
}

function extractExceptionMessage(response: JsonRpc2Response[]): Localizable {
	const description = response
		.filter(JsonRpc2Response.error.isInstance)
		.map(it => it.error.data)
		.filter(Exception.isInstance)
		.map(it => it.description)
		.pop();
	return {
		key: GEBUEHREN_FORDERUNG_KEYS.error.internalServerError,
		args: { reason: { type: "plain", value: description?.default ?? "Unbekannter Fehler." } }
	};
}
